<template>
  <div v-if="products_count > 0" ref="scrollingElement">
    <!-- Filters -->
    <aside :class="['filters-toolbar', { 'filters-toolbar-sticky': filterSticky }]">
      <div class="collection-filters">
        <!-- Sort By -->
        <div v-if="!noProductsFound && sortOptions.length" class="filter-group" v-click-outside="hideSortBy">
          <div
            :class="['filter-button', 'filter-toggle', 'btn', 'btn-secondary', { active: sortByOpen }]"
            @click="
              sortByOpen = !sortByOpen
              filterByOpen = false
            ">
            <span class="filter-title">{{ $language.collection.sort_by }}</span>
          </div>
          <!-- Dropdown -->
          <TransitionExpand>
            <div class="filter-dropdown" v-show="sortByOpen">
              <div
                v-for="(sortOption, index) in sortOptions"
                v-if="!(template !== 'search' && sortOption.handle == 'relevance')"
                :key="index"
                @click="toggleFilter('sort by', sortOption.handle, collectionID)"
                :class="['sort-option', showActive(sortOption.handle)]">
                <span class="sort-option-title">
                  {{ sortOption.label }}
                </span>
              </div>
            </div>
          </TransitionExpand>
        </div>

        <!-- Filter By -->
        <div v-if="filtersToShow.length" class="filter-group" v-click-outside="hideFilterBy">
          <div
            :class="['filter-button', 'filter-toggle', 'btn', 'btn-secondary', { active: filterByOpen }]"
            @click="
              filterByOpen = !filterByOpen
              sortByOpen = false
            ">
            <span class="filter-title">{{ $language.collection.filter_by }}</span>
          </div>
          <!-- Dropdown -->
          <TransitionExpand>
            <div class="filter-dropdown filter-dropdown-filters" v-show="filterByOpen">
              <div
                v-for="(filterGroup, index) in filtersToShow"
                :key="index"
                :class="['filter-group-child', { 'two-columns': filterGroup.values.length >= 10 }]">
                <div
                  :class="['filter-title', 'filter-toggle', { active: filtersOpen.includes(index) }]"
                  @click="
                    toggleFilterBtn(index)
                    sortByOpen = false
                  "
                  v-text="filterGroup.label"></div>
                <TransitionExpand :onlyMobile="true" :isMobile="!$screen.large_up">
                  <div class="filter-dropdown-child" v-show="filtersOpen.includes(index) || $screen.large_up">
                    <div v-if="filterGroup.filterType == 'collection'" class="inner">
                      <div v-for="(option, index) in filterGroup.values" :key="index" class="filter-option">
                        <a
                          :href="'/collections/' + option.handle"
                          class="filter-option-title"
                          v-text="option.label"></a>
                      </div>
                    </div>
                    <div v-else class="inner">
                      <div
                        v-for="(option, index) in filterGroup.values"
                        :key="index"
                        @click="
                          toggleFilter(
                            option.key,
                            stripKey(option.key),
                            collectionID,
                            filterGroup.filterOptionId,
                            $event
                          )
                        "
                        :class="['filter-option', showActive(option.key)]">
                        <div class="filter-option-title">
                          {{ stripKey(option.key, filterGroup.prefix) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </TransitionExpand>
              </div>
              <div v-if="filtersApplied.length" @click="clearFilters()" class="collection-clear-filters">
                <IconClose />{{ $language.collection.clear_filters }}
              </div>
            </div>
          </TransitionExpand>
        </div>
      </div>

      <div class="view-filters">
        <!-- Image toggle -->
        <div class="image-toggle">
          <div @click="updateImageView('outfit')" :class="['image-view-toggle', { active: imageView == 'outfit' }]">
            <div class="image-view-title">{{ $language.collection.outfit_view }}</div>
          </div>
          <div @click="updateImageView('product')" :class="['image-view-toggle', { active: imageView == 'product' }]">
            <div class="image-view-title">{{ $language.collection.product_view }}</div>
          </div>
        </div>

        <!-- Grid toggle -->
        <div class="view-toggle">
          <div
            @click="updateGridSize('small')"
            :class="['small-view-toggle', { active: gridSize == 'small' }]"
            :title="$language.collection.small_view">
            <div class="view-box"></div>
            <div class="view-box"></div>
            <div class="view-box"></div>
            <div class="view-box"></div>
          </div>
          <div
            @click="updateGridSize('large')"
            :class="['large-view-toggle', { active: gridSize == 'large' }]"
            :title="$language.collection.large_view">
            <div class="view-box"></div>
            <div class="view-box"></div>
          </div>
        </div>
      </div>
    </aside>

    <div class="collection-loading" v-if="loading">
      <Loading :activated="true" />
    </div>

    <!-- Product grid -->
    <section
      :class="['product-grid', { 'large-view': gridSize == 'large' }, { 'product-grid-sticky': filterSticky }]"
      v-if="!noProductsFound">
      <template v-for="(item, idx) in products">
        <!-- Product item -->
        <ProductItem
          v-if="item.id"
          :key="item.id"
          :product="item"
          :collection="handle"
          :similar-styles="similarStyles"
          @styling="similarStyling" />

        <!-- Promo block -->
        <PromoItem v-else :key="`${item.insertPosition}-${idx}`" :data="item" />
      </template>

      <!-- Campaign Item -->
      <div v-if="isCampaign && !loading" class="product-grid-campaign">
        <a :href="campaign_product.url">
          <div class="product-image">
            <img
              :src="campaignImage"
              :srcset="campaignImageSrcset"
              sizes="(min-width: 2000px) 950px, (min-width: 990px) calc(50vw - 50px), (min-width: 870px) calc(50vw - 25px), calc(100vw - 40px)"
              :alt="campaign_product.title" />
          </div>
          <div class="product-text">
            <p class="title" v-text="campaign_product.title"></p>
            <p v-if="campaign_product.vendor" class="vendor" v-text="campaign_product.vendor"></p>
            <p v-if="campaign_product.compare_at_price" class="price sale">
              <span itemprop="price" v-text="campaign_product.price"></span>
              <s v-text="campaign_product.compare_at_price"></s>
            </p>
            <p v-else class="price">
              <span itemprop="price" v-text="campaign_product.price"></span>
            </p>
          </div>
        </a>
      </div>
    </section>

    <!-- Pagination -->
    <div v-if="!noProductsFound && totalPages > 1" class="collection-pagination">
      <button
        class="pagination-arrow-left"
        @click="goToPage(currentPage - 1)"
        v-show="currentPage !== 1"
        cloak></button>

      <template v-for="(pageNum, index) in visiblePages">
        <span v-if="pageNum === '...'" :key="`ellipsis-${index}`" class="pagination-ellipsis">...</span>
        <button
          v-else
          :class="['pagination-number', { active: pageNum === currentPage }]"
          :key="pageNum"
          @click="goToPage(pageNum)">
          {{ pageNum }}
        </button>
      </template>

      <button
        class="pagination-arrow-right"
        @click="goToPage(currentPage + 1)"
        v-show="currentPage !== totalPages"
        cloak></button>
    </div>
  </div>

  <!-- No products -->
  <div v-else class="no-results">
    <p v-if="template === 'search'">{{ $language.search.no_results_message }}</p>
    <p v-else>{{ $language.collection.no_matches }}</p>
  </div>
</template>

<script>
import ProductItem from '../Components/ProductItem'
import PromoItem from '../Components/PromoItem'
import IconClose from '../Icons/IconClose'
import TransitionExpand from '../Transitions/TransitionExpand'
import Loading from '../Components/Loading'

export default {
  props: [
    'id',
    'handle',
    'title',
    'products_count',
    'template',
    'query',
    'sort_order',
    'campaign_image',
    'campaign_product',
    'exclude_enable',
    'exclude_collection_ids',
    'promo_blocks'
  ],

  components: { ProductItem, PromoItem, IconClose, TransitionExpand, Loading },

  data() {
    return {
      collectionID: this.id,
      products: [],
      filterOptions: [], // The filters returned by boost commerce API
      filterByOpen: false, // The 'filter by' dropdown is open
      filtersOpen: [], // The filters which are open inside of the 'filter by' dropdown
      filterTagsApplied: [], // The current filters entire tags applied (colour: blue, colour: red)
      filtersApplied: [], // The current filters tag values applied (blue, red)
      sortOptions: [],
      sortByOpen: false, // The 'sort by' dropdown is open
      sortByApplied: '', // The current sort by value applied
      loading: false,
      currentPage: 1,
      noProductsFound: null,
      totalProducts: 0,
      maxVisiblePages: 4,
      imageView: 'outfit',
      gridSize: 'small',
      urlWithFilter: location.pathname,
      /**
       * We've got this set up to HIDE all products tagged with "Lifecycle: Sale" in ALL collections now (so that sale products don't show up in their regular collections, eg Dresses). However, they are launching sale tomorrow so they want to SHOW products tagged with "Lifecycle: Sale" ONLY IN the collections with the collection IDS below (so the sale products DO show up in sale collections, but not in any other collection). - Lance
       * I don't know what PFS do, I just grab the code form their theme and implement them here. - Leo
       */
      // excludeIds: [],
      similarStyles: null,
      excludeIds: this.exclude_collection_ids,
      filterSticky: false
    }
  },

  computed: {
    isCampaign() {
      return (
        this.currentPage == 1 &&
        this.campaign_image &&
        this.campaign_product.url &&
        this.products_count >= 10 &&
        this.gridSize == 'small'
      )
    },

    campaignImage() {
      let image = this.campaign_image
      image += 'w_300,h_221,c_fill'
      return image
    },

    campaignImageSrcset() {
      let image = this.campaign_image,
        imageSrcset = ''

      imageSrcset += image
      imageSrcset += 'w_300,h_221,c_fill 300w, '

      imageSrcset += image
      imageSrcset += 'w_500,h_368,c_fill 500w, '

      imageSrcset += image
      imageSrcset += 'w_750,h_552,c_fill 750w, '

      imageSrcset += image
      imageSrcset += 'w_950,h_699,c_fill 950w, '

      imageSrcset += image
      imageSrcset += 'w_1500,h_1104,c_fill 1500w, '

      imageSrcset += image
      imageSrcset += 'w_1900,h_1398,c_fill 1900w'

      return imageSrcset
    },

    // Only show filters if more than one value (stops 'Shoes' filter on 'Shoes' category etc)
    filtersToShow() {
      let filtersToShowList = []

      this.filterOptions.forEach((filter) => {
        if (filter.values.length > 1) {
          filtersToShowList.push(filter)
        }
      })

      return filtersToShowList
    },

    paginationLimit() {
      return 32
    },

    specialGridSpots() {
      // Make sure the first page has all grid spots filled
      let specialGridSpots = 0

      if (this.currentPage == 1) {
        if (this.isCampaign) {
          specialGridSpots += 2
        }

        if (this.promo_blocks && this.promo_blocks.length) {
          specialGridSpots += this.promo_blocks.length
        }
      }
      return specialGridSpots
    },

    totalPages() {
      return Math.ceil(this.totalProducts / this.paginationLimit)
    },

    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      const maxVisible = this.maxVisiblePages

      if (total <= maxVisible) {
        // Show all pages if total is less than max visible
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        // Always show first page
        pages.push(1)

        let start = Math.max(2, current - 1)
        let end = Math.min(total - 1, current + 1)

        // If near the beginning
        if (current <= 2) {
          end = Math.min(total - 1, maxVisible - 1)
        }

        // If near the end
        if (current >= total - 1) {
          start = Math.max(2, total - (maxVisible - 2))
        }

        // Add ellipsis after first page if needed
        if (start > 2) {
          pages.push('...')
        }

        // Add middle pages
        for (let i = start; i <= end; i++) {
          pages.push(i)
        }

        // Add ellipsis before last page if needed
        if (end < total - 1) {
          pages.push('...')
        }

        // Always show last page
        pages.push(total)
      }

      return pages
    }
  },

  mounted() {
    // Check for page parameter
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get('page')
    if (pageParam && !isNaN(pageParam)) {
      this.currentPage = parseInt(pageParam)
    }

    this.loadCollection()

    if (window.localStorage.getItem('collection_image_view')) {
      this.imageView = window.localStorage.getItem('collection_image_view')
    }

    if (window.localStorage.getItem('collection_view')) {
      this.gridSize = window.localStorage.getItem('collection_view')
    }

    // Similiar Styling Configs
    let data = document.querySelector('script#SimilarStyling')
    if (data) this.similarStyles = JSON.parse(data.innerText)

    // Browsers back/forward navigation - update pagination and scroll to top
    window.addEventListener('popstate', async () => {
      const currentParams = new URLSearchParams(window.location.search)
      const page = parseInt(currentParams.get('page')) || 1
      this.currentPage = page
      this.products = []
      await this.getFilteredCollection({ collectionID: this.id, page })
      window.scrollTo({ top: 0, behavior: 'instant' })
    })

    window.addEventListener('scroll', () => {
      const rect = this.$refs.scrollingElement.getBoundingClientRect()
      const elementTop = rect.top
      let headerHeight = 79
      if (window.innerWidth > 989) {
        headerHeight = 64
      }
      if (elementTop <= headerHeight) {
        this.filterSticky = true
      } else {
        this.filterSticky = false
      }
    })
  },

  methods: {
    async loadCollection() {
      window.sessionStorage.removeItem('similar_styling')

      // get url with params - filters
      const urlParams = new URLSearchParams(location.search)
      let filterArray = []

      // Build filter params string
      const filterParams = new URLSearchParams()
      const nonFilterParams = ['page', 'q', 'sort'] // Parameters that are not filters
      for (const [key, value] of urlParams.entries()) {
        if (!nonFilterParams.includes(key)) {
          filterParams.append(key, value)
        }
      }

      // Set urlWithFilter (pathname + filter params)
      this.urlWithFilter = location.pathname + (filterParams.toString() ? '?' + filterParams.toString() : '')

      // Process filter parameters
      for (const [key, value] of urlParams.entries()) {
        if (!nonFilterParams.includes(key)) {
          const decodedValue = decodeURIComponent(value).replace('%20&%20', ' & ')
          const filterString = `${key}=${decodedValue}`
          filterArray.push(filterString)
          this.filterTagsApplied.push(`${key}|||${decodedValue}`)
          this.filtersApplied.push(decodedValue)
        }
      }

      await this.getFilteredCollection({ collectionID: this.id, page: this.currentPage })

      if (filterArray) {
        filterArray.forEach((filter) => {
          let title = filter.split('=')[0].split('_'),
            value = filter.split('=')[1]

          document.querySelectorAll('.filter-title.filter-toggle').forEach((item) => {
            const filter = item.textContent.replace(/(\n\t|\n|\t)/gm, '')
            if (filter.toLowerCase() === title[title.length - 1]) {
              item.nextElementSibling.querySelectorAll('.filter-option').forEach((option) => {
                const activeOption = option.textContent.replace(/(\n\t|\n|\t)/gm, '')

                if (value === activeOption) option.classList.add('active')
              })
            }
          })
        })
      }

      // Build sorting
      // If theme settings retrieved
      if ('general' in theme.filterSettings) {
        theme.filterSettings.general.customSortingList.split('|').forEach((sortOption) => {
          this.sortOptions.push({
            handle: sortOption,
            label: theme.filterSettings.label.sortByOptions[sortOption]
          })
        })
      }

      this.sortByApplied = this.sort_order

      this.noProductsFound = !this.products.length
    },

    toggleFilterBtn(idx) {
      if (this.filtersOpen.includes(idx)) {
        let index = this.filtersOpen.indexOf(idx)
        this.filtersOpen.splice(index, 1)
      } else {
        this.filtersOpen.push(idx)
      }
    },

    toggleFilter(tag, value, collectionID, filterOptionId) {
      // Reset products
      this.products = []

      if (!this.filtersApplied.includes(tag)) {
        // Add filter
        if (tag == 'sort by') {
          this.sortByApplied = value
        } else {
          this.filterTagsApplied.push(`${filterOptionId}|||${tag}`)
          this.filtersApplied.push(tag)

          // Add filter to URL using URLSearchParams
          const url = new URL(window.location)
          url.searchParams.set(filterOptionId, tag)
          url.searchParams.delete('page') // Reset to page 1
          this.urlWithFilter = url.pathname + '?' + url.searchParams.toString()
          history.pushState('', '', url)
        }
      } else {
        // Remove filter
        this.filterTagsApplied.splice(this.filterTagsApplied.indexOf(`${filterOptionId}|||${tag}`), 1)
        this.filtersApplied.splice(this.filtersApplied.indexOf(tag), 1)

        // Remove filter from URL using URLSearchParams
        const url = new URL(window.location)
        url.searchParams.delete(filterOptionId)
        url.searchParams.delete('page') // Reset to page 1
        const searchString = url.searchParams.toString()
        this.urlWithFilter = url.pathname + (searchString ? '?' + searchString : '')
        history.pushState('', '', this.urlWithFilter)
      }

      // Reset to page 1
      this.currentPage = 1

      this.getFilteredCollection({ collectionID: this.id, page: 1 })
    },

    clearFilters() {
      this.filterTagsApplied = []
      this.filtersApplied = []
      this.products = []

      // Reset to page 1 and clear all URL parameters (filters and page)
      this.currentPage = 1
      this.urlWithFilter = location.pathname
      history.replaceState('', '', location.pathname)

      this.getFilteredCollection({ collectionID: this.id, page: 1 })
    },

    stripKey(tag, prefix) {
      if (/:(.+)/.exec(tag) && /:(.+)/.exec(tag).length) {
        tag = /:(.+)/.exec(tag)[1].trim()
      }

      if (prefix) {
        let prefixStripped = prefix.replace('\\-', '-')
        return tag.replace(prefixStripped, '').trim()
      } else {
        return tag
      }
    },

    showActive(option) {
      if (this.filtersApplied.includes(option)) return 'active'
      if (this.sortByApplied.includes(option)) return 'active'
    },

    async getFilteredCollection({ collectionID, page }) {
      this.loading = true
      // BCSF Endpoint
      let url = new URL('https://services.mybcapps.com/bc-sf-filter/')

      // Request options
      let params = {
        shop: theme.domain,
        page: page,
        limit: this.paginationLimit,
        product_available: true,
        variant_available: true,
        build_filter_tree: true,
        collection_scope: collectionID
      }

      // Collection vs Search
      if (this.query) {
        url.pathname = url.pathname + 'search'
        params.q = this.query
      } else {
        url.pathname = url.pathname + 'filter'
      }

      // Set sort order
      if (this.sortByApplied) {
        params.sort = this.sortByApplied
      } else {
        params.sort = this.sort_order
      }

      // Create query string from params object
      Object.keys(params).forEach((key) => {
        url.searchParams.append(key, params[key])
      })

      // Insert filters
      if (this.filterTagsApplied.length) {
        // let tags = []
        this.filterTagsApplied.forEach((filter) => {
          let filterKey = filter.split('|||')[0]
          let filterVal = filter.split('|||')[1]
          url.searchParams.append(`${filterKey}[]`, filterVal)
        })
      }

      // Send request
      await fetch(url)
        .then((response) => response.json())
        .then((data) => {
          this.loading = false
          let products = data.products

          if (products.constructor === Object) {
            // 0 products to show
            this.products = []
          } else {
            // Splice promo blocks into products array
            if (page === 1) {
              const total_product = data.total_product
              let newContent = `${total_product} results for "${this.query}"`
              if (total_product == 0) {
                newContent = `0 result for "${this.query}"`
              } else if (total_product == 1) {
                newContent = `1 result for "${this.query}"`
              }
              const ele = document.querySelector('.search-header .results-count')
              if (ele) {
                ele.textContent = newContent
                ele.classList.remove('display-none')
              }
              this.promo_blocks?.forEach((block) => {
                const insertPosition = Number(block.insertPosition) - 1
                products.splice(insertPosition, 0, block)
              })
            }

            this.products = products.splice(0, this.paginationLimit - this.specialGridSpots + 1)
            this.totalProducts = data.total_product
            // Build filter tree
            this.filterOptions = data.filter.options.filter((group) => group.values.length)
          }
        })
        .catch((error) => {
          console.log('error', error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    async goToPage(pageNumber) {
      if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) {
        return
      }

      this.currentPage = pageNumber
      this.products = []

      // Update URL with page parameter
      const url = new URL(window.location)
      if (pageNumber === 1) {
        url.searchParams.delete('page')
      } else {
        url.searchParams.set('page', pageNumber)
      }
      window.history.pushState({}, '', url)

      // Scroll to top of products
      window.scrollTo({
        top: 0,
        behavior: 'instant'
      })

      await this.getFilteredCollection({ collectionID: this.id, page: pageNumber })
    },

    updateImageView(view) {
      this.imageView = view
      window.localStorage.setItem('collection_image_view', view)
    },

    updateGridSize(size) {
      this.gridSize = size
      window.localStorage.setItem('collection_view', size)
    },

    hideSortBy() {
      this.sortByOpen = false
    },

    hideFilterBy() {
      this.filterByOpen = false
    },

    similarStyling: function (e) {
      // check collection
      if (this.id !== '') {
        window.sessionStorage.setItem('similar_styling', JSON.stringify(e))
        window.location.href = '/collections/all'
        return
      }
      this.filtersApplied = []
      this.filterTagsApplied = []
      for (let x of e.params) {
        let k = x['key'],
          v = x['value']
        this.filterTagsApplied.push(`${k}|||${v}`)
        this.filtersApplied.push(v)
      }
      // Reset products
      this.products = []

      // Reset to page 1 and remove page parameter from URL
      this.currentPage = 1
      const url = new URL(window.location)
      url.searchParams.delete('page')
      history.replaceState('', '', url)

      this.getFilteredCollection({ collectionID: this.id, page: 1 })
    }
  }
}
</script>
